import React, { useEffect, useState, useCallback } from 'react';
import { useAsync } from 'react-use-v13';
import { makeStyles, Checkbox, Button } from 'react-scale/components/design-system';
import apiFetch from '@scale/frontend-lib/api-fetch';
import {
  TaskerJourneyResponse,
  TaskerJourneyEntryType,
  ProjectJourneyEntry,
  MAX_IDS_SHOWN,
} from '@scale/shared/types/api/corp-api/taskerJourney/TaskerJourneyResponse';
import _ from 'lodash';
import DateFilterWrapper from 'react-scale/dashboard.scale.com/components/SelfServe/ProjectLabelersTablePage/DateFilterWrapper';
import moment, { Moment } from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
interface ProjectJourneyEntryProps {
  entry: ProjectJourneyEntry;
}

const ProjectJourneyEntryRow: React.FC<ProjectJourneyEntryProps> = ({ entry }) => {
  const classes = useStyles();

  const [expanded, setExpanded] = useState(false);

  return (
    <div>
      <div className={classes.rowProject} onClick={() => setExpanded(!expanded)}>
        <div className={classes.id}>
          <FontAwesomeIcon
            icon={expanded ? 'caret-up' : 'caret-down'}
            style={{ marginRight: '6px' }}
          />
          {entry.type}
        </div>
        <div className={classes.type}>{entry.length}</div>
        <div className={classes.addProject}>{entry.extra}</div>
      </div>
      {expanded && (
        <div className={classes.idList}>
          {entry.ids.map(e => {
            return <span key={e}>{e}</span>;
          })}
          {entry.length > MAX_IDS_SHOWN && (
            <span>... list abridged (first {MAX_IDS_SHOWN} shown)</span>
          )}
        </div>
      )}
    </div>
  );
};

interface TaskerJourneyExplanationProps {
  workerId?: string;
  projectId?: string;
}

export const TaskerJourneyExplanation: React.FC<TaskerJourneyExplanationProps> = props => {
  const classes = useStyles();
  const [startDate, setStartDate] = useState<Moment | undefined>();
  const [endDate, setEndDate] = useState<Moment | undefined>(moment());
  const [dateReset, setDateReset] = useState(false);
  const [filters, setFilters] = useState<TaskerJourneyEntryType[]>([]);

  const { value: response, error } = useAsync<TaskerJourneyResponse>(
    () =>
      props.projectId
        ? apiFetch(null, 'corp-api/explain_tasker_journey', {
            query: {
              worker: props.workerId,
              project: props.projectId,
              startDate: startDate ? startDate.toDate() : undefined,
              endDate: endDate ? endDate.toDate() : undefined,
            },
          })
        : Promise.reject(new Error('Project ID is required.')),
    [dateReset],
  );

  useEffect(() => {
    setFilters(Object.values(TaskerJourneyEntryType));
    if (response && response.startDate) {
      setStartDate(moment(response.startDate));
    }
  }, [response]);

  const toggleFilter = useCallback(
    (t: any) => {
      if (filters.includes(t)) {
        setFilters(filters.filter(a => a !== t));
      } else {
        const c = _.cloneDeep(filters);
        c.push(t);
        setFilters(c);
      }
    },
    [filters],
  );

  const displayText = (type: string) => {
    return type === TaskerJourneyEntryType.CourseProgress ? 'Course Completed' : type;
  };

  return (
    <>
      {!error && !response && <div>Loading...</div>}
      {response && response.entries && (
        <div className={classes.container}>
          <div className={classes.checkRow}>
            {Object.values(TaskerJourneyEntryType).map(t => {
              return (
                <Checkbox
                  key={t}
                  checked={filters.includes(t)}
                  label={`${t} (${
                    response.entries && response.entries.filter(entry => entry.type === t).length
                  })`}
                  onChange={() => {
                    toggleFilter(t);
                  }}
                />
              );
            })}
          </div>
          {response.entries
            .filter(entry => filters.includes(entry.type))
            .map(entry => {
              return (
                <div key={`${entry.id}${entry.type}${entry.date}`} className={classes.row}>
                  <div className={classes.type}>{displayText(entry.type)}</div>
                  <div className={classes.id}>
                    <a href={`/corp/lookup/${entry.id}`} target="_blank" rel="noopener noreferrer">
                      {entry.id}
                    </a>
                  </div>
                  <div className={classes.add}>{entry.extra}</div>
                  <div className={classes.date}>
                    {entry.date ? new Date(entry.date).toLocaleString() : 'Incomplete'}
                  </div>
                </div>
              );
            })}
        </div>
      )}
      {response && response.projectEntries && (
        <div className={classes.container}>
          <div className={classes.dateRow}>
            <DateFilterWrapper
              startDate={startDate}
              endDate={endDate}
              setStartDate={setStartDate}
              setEndDate={setEndDate}
              maxNumDays={180}
            />
            <Button type="primary" onClick={() => setDateReset(!dateReset)}>
              Filter
            </Button>
          </div>
          <div className={classes.projectList}>
            {response.projectEntries.map(entry => {
              return <ProjectJourneyEntryRow key={`}${entry.type}`} entry={entry} />;
            })}
          </div>
        </div>
      )}
      {error && <div>{JSON.stringify(error)}</div>}
    </>
  );
};

const useStyles = makeStyles(() => ({
  container: {
    margin: '16px 0',
    padding: '16px',
    border: '1px solid rgba(0, 0, 0, 0.05)',
    borderRadius: '8px',
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    height: '35px',
  },
  projectList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  dateRow: {
    display: 'flex',
    gap: '25px',
    padding: '16px',
  },
  type: {
    width: '120px',
  },
  id: {
    width: '200px',
  },
  date: {
    width: '200px',
  },
  add: {
    width: '400px',
  },
  checkRow: {
    marginBottom: '15px',
  },
  addProject: {
    width: '600px',
  },
  idList: {
    display: 'flex',
    width: '100%',
    flexWrap: 'wrap',
    gap: '10px',
    padding: '10px',
  },
  rowProject: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    height: '50px',
    cursor: 'pointer',
    border: '1px solid rgba(0, 0, 0, 0.2)',
    borderRadius: '8px',
    padding: '10px',
    alignItems: 'center',
  },
}));
